import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { xcg, humanWaiting, cwPrettyDate, hoursSince } from '../lib/format';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';
import { useRegisterStatus } from '../context/RegisterStatus';

export default function Payments() {
  const [bookings, setBookings] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmingBooking, setConfirmingBooking] = useState(null);
  const [rejectingBooking, setRejectingBooking] = useState(null);
  const [reason, setReason] = useState('');
  const [zoomProof, setZoomProof] = useState(null);
  const { isStale } = useRegisterStatus() || {};

  const load = useCallback(async () => {
    const { data } = await api.get('/bookings');
    setBookings(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const queue = useMemo(() => {
    if (!bookings) return [];
    return bookings
      .filter(b => b.PaymentStatus !== 'Complete' && b.Status !== 'Cancelled-Released')
      .sort((a, b) => a.CreatedAt.localeCompare(b.CreatedAt));
  }, [bookings]);

  const doConfirm = async (bn) => {
    setBusy(bn);
    try {
      await api.post('/bookings/confirm-payment', { booking_number: bn });
      toast.success('Payment confirmed. The guest is notified automatically.');
      setConfirmingBooking(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update the booking.');
    } finally { setBusy(null); }
  };

  const doReject = async (bn) => {
    if (!reason.trim()) { toast.error('Please enter a reason'); return; }
    setBusy(bn);
    try {
      await api.post('/bookings/mark-incomplete', { booking_number: bn, reason });
      toast.success('Marked as incomplete.');
      setRejectingBooking(null);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update the booking.');
    } finally { setBusy(null); }
  };

  if (!bookings) return <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Payments to verify</h1>
        <p className="text-[#333]/70 mt-1">Look at the proof. <em>You</em> confirm each payment. Oldest first.</p>
      </div>

      {queue.length === 0 ? (
        <div className="text-[#333]/60 bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl p-8 text-center">
          Nothing to review right now. Nice work.
        </div>
      ) : (
        <ul className="space-y-5">
          {queue.map((b) => (
            <li key={b['Booking#']} className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <button
                  data-testid={`proof-${b['Booking#']}`}
                  onClick={() => setZoomProof(b.ProofURL)}
                  className="block bg-[#F5F5F5] group"
                >
                  <img
                    alt={`Payment proof for ${b.Name}`}
                    src={b.ProofURL}
                    className="w-full h-64 md:h-full object-cover group-hover:opacity-95"
                  />
                </button>
                <div className="p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading text-xl text-[#1F5A24]">{b.Name}</div>
                      <div className="text-sm text-[#333]/70">{b['Booking#']}</div>
                    </div>
                    <StatusBadge status={b.PaymentStatus} />
                  </div>

                  <dl className="grid grid-cols-2 gap-3 mt-4 text-sm">
                    <div><dt className="text-[#333]/60 text-xs uppercase tracking-widest">Amount</dt><dd className="font-medium">{xcg(b.Amount)}</dd></div>
                    <div><dt className="text-[#333]/60 text-xs uppercase tracking-widest">Method</dt><dd>{b.PaymentMethod}</dd></div>
                    <div><dt className="text-[#333]/60 text-xs uppercase tracking-widest">Check in</dt><dd>{cwPrettyDate(b.CheckIn)}</dd></div>
                    <div><dt className="text-[#333]/60 text-xs uppercase tracking-widest">Submitted</dt><dd>{humanWaiting(b.CreatedAt)}</dd></div>
                  </dl>

                  <div className="mt-auto pt-5 grid grid-cols-2 gap-3">
                    <button
                      data-testid={`confirm-btn-${b['Booking#']}`}
                      onClick={() => setConfirmingBooking(b)}
                      disabled={busy === b['Booking#']}
                      className="px-4 py-3 min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium disabled:opacity-60"
                    >
                      Confirm payment
                    </button>
                    <button
                      data-testid={`reject-btn-${b['Booking#']}`}
                      onClick={() => { setRejectingBooking(b); setReason(''); }}
                      disabled={busy === b['Booking#']}
                      className="px-4 py-3 min-h-[3rem] rounded-lg border border-[#CFD8CF] hover:bg-[#FFF3E0] text-[#B26A00] font-medium disabled:opacity-60"
                    >
                      Mark incomplete
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Confirm dialog */}
      {confirmingBooking && (
        <Modal onClose={() => setConfirmingBooking(null)} testid="confirm-dialog">
          <h3 className="font-heading text-xl text-[#1F5A24]">Confirm this payment?</h3>
          <p className="text-sm text-[#333]/80 mt-2">
            You are confirming that you saw proof of <strong>{xcg(confirmingBooking.Amount)}</strong> from{' '}
            <strong>{confirmingBooking.Name}</strong>. This turns the hold into a real booking.
          </p>
          <div className="mt-5 flex gap-3 justify-end">
            <button onClick={() => setConfirmingBooking(null)} className="px-4 py-3 rounded-lg border border-[#CFD8CF] hover:bg-[#F5F5F5]">Cancel</button>
            <button
              data-testid="confirm-yes"
              disabled={busy === confirmingBooking['Booking#']}
              onClick={() => doConfirm(confirmingBooking['Booking#'])}
              className="px-4 py-3 rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium min-w-[10rem] disabled:opacity-60"
            >
              Yes, confirm {xcg(confirmingBooking.Amount)}
            </button>
          </div>
        </Modal>
      )}

      {/* Reject dialog */}
      {rejectingBooking && (
        <Modal onClose={() => setRejectingBooking(null)} testid="reject-dialog">
          <h3 className="font-heading text-xl text-[#1F5A24]">Mark payment incomplete</h3>
          <p className="text-sm text-[#333]/80 mt-2">Guest: <strong>{rejectingBooking.Name}</strong> · {xcg(rejectingBooking.Amount)}</p>
          <label className="block text-sm font-medium mt-4 mb-1">Reason (required)</label>
          <textarea
            data-testid="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. amount short by 40 XCG"
            className="w-full bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2E7D32] outline-none"
          />
          <div className="mt-5 flex gap-3 justify-end">
            <button onClick={() => setRejectingBooking(null)} className="px-4 py-3 rounded-lg border border-[#CFD8CF]">Cancel</button>
            <button
              data-testid="reject-yes"
              disabled={busy === rejectingBooking['Booking#']}
              onClick={() => doReject(rejectingBooking['Booking#'])}
              className="px-4 py-3 rounded-lg bg-[#B26A00] hover:bg-[#8f5500] text-white font-medium disabled:opacity-60"
            >
              Mark incomplete
            </button>
          </div>
        </Modal>
      )}

      {/* Zoom proof */}
      {zoomProof && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={() => setZoomProof(null)} data-testid="zoom-proof">
          <img src={zoomProof} alt="Payment proof large" className="max-w-full max-h-full rounded-lg" />
          <button className="absolute top-4 right-4 p-2 bg-white/90 rounded-full" onClick={() => setZoomProof(null)}><X size={20}/></button>
        </div>
      )}
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid={testid}>
      <div className="bg-[#FBFCFB] rounded-2xl border border-[#CFD8CF] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
